'use client';

import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Heading, LoadingIndicator } from '@petsmart-ui/sparky';

import { logSentryError, SentryAcpAutoshipErrors as SentryErrors, useUserSession } from '@libs/common-next';
import { OG_COOKIE } from '@libs/core/constants';

import './AutoshipPage.scss';

import { setOgAuthCookie, attachTracking } from './utils';
import { trackEvent } from './EventTracker';
import { useOrdergrooveMsi } from './useOrdergrooveMsi';

interface WindowWithDataLayer extends Window {
    triggerDataLayerEvent?: typeof trackEvent;
}

export function AutoshipPage() {
    const { user, userData } = useUserSession();

    const [isOgCookieSet, setIsOgCookieSet] = useState(Boolean(Cookies.get(OG_COOKIE)));
    const [isOgScriptLoading, setIsOgScriptLoading] = useState(true);

    useOrdergrooveMsi({
        isOgCookieSet,
        onLoad: () => {
            setIsOgScriptLoading(false);
            (window as WindowWithDataLayer).triggerDataLayerEvent = trackEvent;
        },
        onError: () => {
            setIsOgScriptLoading(false);
            logSentryError(
                new Error('Failed to load Ordergroove script'),
                SentryErrors.AutoshipPage.LoadOrdergrooveScriptFailed
            );
        },
    });

    useEffect(() => {
        // Async function to set the Ordergroove authentication cookie
        const setCookie = async () => {
            if (isOgCookieSet) {
                // If the cookie is already set, no need to proceed
                return;
            }

            // Ensure both user SID and email are available before proceeding
            if (!user?.sid || !userData?.profile?.emailAddress) {
                setIsOgCookieSet(false);
                return;
            }

            // Attempt to set the OG auth cookie with user info
            const isOgCookieSuccessfullySet = await setOgAuthCookie({ userSid: user.sid });

            // If the cookie is set successfully, update the state
            setIsOgCookieSet(isOgCookieSuccessfullySet);
        };

        void setCookie();
    }, [isOgCookieSet, user?.sid, userData?.profile?.emailAddress]);

    useEffect(() => {
        const targetNode = document.getElementById('og-msi');
        if (!targetNode) return;

        const observer = new MutationObserver(() => {
            attachTracking(targetNode, '.no-autoship__btn', 'Shop now');
            attachTracking(targetNode, '.shop-for-more-items', 'Shop for more items');
        });

        observer.observe(targetNode, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="autoship-page" data-testid="autoship-page">
            <Heading tagName="h5" data-testid="heading">
                Autoship
            </Heading>

            {/* Ordergroove container */}
            <div id="og-msi" data-testid="og-msi" data-cs-mask />

            {isOgScriptLoading && (
                <div className="loader-container">
                    <LoadingIndicator iconSize="4xl" data-testid="loading-indicator" />
                </div>
            )}
        </div>
    );
}

// imported as dynamic import - do NOT remove
export default AutoshipPage;
